import { collection, getDocs, query, where } from 'firebase/firestore';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCountryMarketState } from '../../controllers/CountryMarketController/CountryMarketState';
import { db } from '../../firebase';
import FormulationRecommendationPlaceholderImage from '../../images/formulation-recommendation-placeholder.png';
import {
  isKeyInProductApplications,
  isTaxonomyWithKeyMultiselect,
} from '../../lib/briefing';
import { useBriefingState } from '../Briefing/BriefingState';
import {
  Recommendation,
  RecommendationsSection,
} from './RecommendationsSection';

export type Formulation = {
  name: string;
  reference: string;
  description: string;
  applications: string[];
  /**
   * relates to the requirements satisfied by the formulation
   * e.g. requirementsCan, requirementsMust
   */
  marketing_claims: string[];
  product_skus: string[];
  market: string;
  image_url: string;
  formulation_url: {
    [key: string]: string;
  };
  filter_terms?: string[];
};

type FormulationRecommendationsProps = {
  initialLimit: number;
  reportRecommendationsExist?: (exists: boolean) => void;
};

export const FormulationRecommendations: React.FC<
  FormulationRecommendationsProps
> = ({
  initialLimit,
  reportRecommendationsExist,
}: FormulationRecommendationsProps) => {
  const { t, i18n } = useTranslation();

  const languageKey = i18n.language;
  const defaultLanguageKey = 'en_US';
  const [
    {
      productApplications,
      requirementsMust,
      requirementsCan,
      azelisRawMaterials,
      rawMaterial,
    },
  ] = useBriefingState();

  const [{ countryMarketConfig }] = useCountryMarketState();

  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);

  const getByLanguageKey = useCallback(
    (obj: { [key: string]: string }) =>
      obj[languageKey] ?? obj[defaultLanguageKey],
    [languageKey],
  );

  const transformFormulationToRecommendation = useCallback(
    (formulation: Formulation): Recommendation => ({
      ...formulation,
      url: getByLanguageKey(formulation.formulation_url),
    }),
    [getByLanguageKey],
  );

  /**
   * Calculates a fitness score for any formulation with the
   * given restrictions and requirements of the user.
   *
   * The *smaller* the score, the *better* it fits!
   *
   * `0` means, the formulation doesn't fit at all,
   * negative values are good.
   */
  const calculateScore = useCallback(
    (formulation: Formulation): number =>
      -[
        // Score formulation based on how many requirementsMust they meet
        requirementsMust?.filter(({ value }) =>
          formulation.marketing_claims.includes(value.toString()),
        ),
        // Score formulation based on how many requirementsCan they meet
        requirementsCan?.filter(({ value }) =>
          formulation.marketing_claims.includes(value.toString()),
        ),
        // Score formulation based on how many azelisRawMaterials they contain
        azelisRawMaterials?.filter(({ value }) =>
          formulation.product_skus.includes(value.toString()),
        ),
        // Score formulation based on whether its name is in the rawMaterial string
        rawMaterial?.includes(formulation.name),
      ]
        .flat()
        .filter((scoreItem) => !!scoreItem).length,
    [azelisRawMaterials, rawMaterial, requirementsCan, requirementsMust],
  );

  useEffect(() => {
    const firebaseCountryMarketCollection = `${countryMarketConfig?.countryCluster}_${countryMarketConfig?.marketDisplayName}`;

    const deepestApplicationKeysSelected = productApplications
      ?.filter(
        (productApplication) =>
          !isKeyInProductApplications('other', [productApplication]),
      )
      .map((productApplication) =>
        isTaxonomyWithKeyMultiselect(productApplication)
          ? Object.values(productApplication).map(({ key }) => key)
          : [productApplication.key],
      )
      .reverse()[0];

    if (!deepestApplicationKeysSelected) return;

    const getRecommendations = async () => {
      const q = query(
        collection(
          db,
          `recommendations/${firebaseCountryMarketCollection}/formulations`,
        ),
        where(
          'applications',
          'array-contains-any',
          deepestApplicationKeysSelected,
        ),
      );

      const querySnapshot = await getDocs(q);

      const recommendations = [] as any[];

      querySnapshot.forEach((doc) => recommendations.push(doc.data()));

      const mappedRecommendations = recommendations
        .map((formulation) => ({
          formulation,
          score: calculateScore(formulation),
        }))
        .sort((a, b) => a.score - b.score)
        .map(({ formulation }) =>
          transformFormulationToRecommendation(formulation),
        );

      setRecommendations(mappedRecommendations);
    };

    getRecommendations();
  }, [
    calculateScore,
    countryMarketConfig,
    productApplications,
    transformFormulationToRecommendation,
  ]);

  return (
    <RecommendationsSection
      initialLimit={initialLimit}
      recommendations={recommendations}
      recommendationType={'formulation'}
      heading={t('topics:formulations:name')}
      placeholderImage={FormulationRecommendationPlaceholderImage}
      reportRecommendationsExist={reportRecommendationsExist}
    />
  );
};
